import { Injectable, inject } from '@angular/core';
import { EcaSetupConfigurationService, SetupConfigAttribute } from '@drc-eca/eca-components-lib';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class OktaTenantService {
  private setupConfigService = inject(EcaSetupConfigurationService);
  private readonly deocsProductCode = 'DEOCS';

  static isOpaTenantEnabled(): boolean {
    return ['sqa', 'staging', 'production'].includes(environment.apiGatewayConfig.deployment);
  }

  private get defaultSamlRedirectUrl(): string {
    // TODO: This env fallback is still needed for non-OPA clients and no-client
    // flows until every environment/client is migrated to setup-config-driven
    // okta-saml-redirect-url resolution.
    return environment.okta.samlRedirectUrl;
  }

  private isOpaProduct(productCode?: string): boolean {
    return OktaTenantService.isOpaTenantEnabled() && productCode?.toUpperCase() === this.deocsProductCode;
  }

  private getOverrideRedirectUrl(attributes: SetupConfigAttribute[]): string | undefined {
    return attributes.find((attr) => attr.attribute?.key === 'okta-saml-redirect-url')?.value;
  }

  getSamlRedirectUrl(productCode?: string): Observable<string> {
    if (!productCode || (productCode.toUpperCase() === this.deocsProductCode && !OktaTenantService.isOpaTenantEnabled())) {
      return of(this.defaultSamlRedirectUrl);
    }

    return this.setupConfigService.getAttributesForClient(productCode).pipe(
      map((attributes: SetupConfigAttribute[]) => {
        const override = this.getOverrideRedirectUrl(attributes);
        if (override) {
          return override;
        }

        if (this.isOpaProduct(productCode)) {
          throw new Error(`Missing required setup-config attribute okta-saml-redirect-url for product ${productCode}`);
        }

        return this.defaultSamlRedirectUrl;
      }),
      catchError((error) => this.isOpaProduct(productCode)
        ? throwError(() => error)
        : of(this.defaultSamlRedirectUrl)
      )
    );
  }

  getTenantOrigin(productCode?: string): Observable<string> {
    return this.getSamlRedirectUrl(productCode).pipe(
      map((samlRedirectUrl) => new URL(samlRedirectUrl).origin),
      catchError((error) => this.isOpaProduct(productCode)
        ? throwError(() => error)
        : of(new URL(this.defaultSamlRedirectUrl).origin)
      )
    );
  }
}